import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useApi } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import type { PagedResult } from "@/lib/pagination";
import { RESOURCE_TYPE_LABELS } from "@/lib/resourceTypeLabels";
import { ReachableBadge } from "./ReachableBadge";
import { ThroughConnector, type RoutingConnector } from "./ThroughConnector";
import { LoadError } from "./LoadError";
import { EmptyState } from "./EmptyState";

const MAX_SHOWN = 100;

const COLUMNS = ["Resource", "Type", "Reachable", "Last checked"] as const;

interface BaseResource {
  id: string;
  name: string;
  type: string;
  kind: string | null;
  address: string;
  port: number;
}

interface AssignedResourcesTableProps<TResource extends BaseResource> {
  endpoint: string;
  emptyTitle: string;
  emptyMessage: string;
  getAssignment: (
    resource: TResource,
  ) =>
    | {
        reachable: boolean | null;
        lastCheckedAt: string | null;
        online: boolean;
      }
    | undefined;
  getRoutingConnectors?: (resource: TResource) => RoutingConnector[];
}

function formatAbsolute(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3.5 w-36 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-28 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-5 w-24 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-24 rounded bg-surface" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function AssignedResourcesTable<TResource extends BaseResource>({
  endpoint,
  emptyTitle,
  emptyMessage,
  getAssignment,
  getRoutingConnectors,
}: AssignedResourcesTableProps<TResource>) {
  const { accessToken } = useAuth();
  const api = useApi();
  const [resources, setResources] = useState<TResource[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadResources = useCallback(() => {
    if (!accessToken) {
      return;
    }

    void api
      .get<PagedResult<TResource>>(`${endpoint}?page=1&pageSize=${MAX_SHOWN}`)
      .then((data) => {
        if (data) {
          setResources(data.items);
          setTotalCount(data.totalCount);
          setError(null);
        } else {
          setError("Couldn't load assigned resources.");
        }
      });
  }, [accessToken, api, endpoint]);

  useEffect(loadResources, [loadResources]);

  useEffect(() => {
    const interval = setInterval(loadResources, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadResources]);

  function retry() {
    setError(null);
    setResources(null);
    loadResources();
  }

  if (error && !resources) {
    return <LoadError what="assigned resources" onRetry={retry} />;
  }

  if (resources && resources.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyMessage} />;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] text-left text-sm">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!resources ? (
              <SkeletonRows />
            ) : (
              resources.map((resource) => {
                const assignment = getAssignment(resource);
                const routingConnectors =
                  getRoutingConnectors?.(resource) ?? [];
                const throughConnector = routingConnectors.length > 0;

                return (
                  <tr
                    key={resource.id}
                    className="h-14 border-b border-surface/70 hover:bg-surface"
                  >
                    <td className="py-2 pr-4">
                      <div>{resource.name}</div>
                      <div className="font-mono text-xs text-foreground/70">
                        {resource.address}:{resource.port}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      {RESOURCE_TYPE_LABELS[resource.type] ?? resource.type}
                    </td>
                    <td className="py-2 pr-4">
                      {throughConnector ? (
                        <ThroughConnector
                          connectors={routingConnectors}
                          linked
                        />
                      ) : (
                        <ReachableBadge
                          reachable={assignment?.reachable}
                          online={assignment?.online}
                        />
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {throughConnector ? (
                        <span className="text-foreground/70">—</span>
                      ) : assignment?.lastCheckedAt ? (
                        <>
                          <div className="tabular-nums">
                            {formatRelativeTime(assignment.lastCheckedAt)}
                          </div>
                          <div className="text-xs tabular-nums text-foreground/70">
                            {formatAbsolute(assignment.lastCheckedAt)}
                          </div>
                        </>
                      ) : (
                        <span className="text-foreground/70">Never</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalCount > MAX_SHOWN && (
        <p className="mt-3 text-sm text-foreground/70">
          Showing the first {MAX_SHOWN} of {totalCount}.
        </p>
      )}
    </div>
  );
}
